import { fetchAuthSession, signOut } from "@aws-amplify/auth";
import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { assignWith } from "lodash";
import { VITE_BASE_API_URL, VITE_FAST_API_URL } from "./static/importEnv";

const baseUrl = VITE_BASE_API_URL;
const fastApiBaseUrl = VITE_FAST_API_URL;

export const axiosInstance = axios.create({
  baseURL: baseUrl,
});

export const publicAxiosInstance = axios.create({
  baseURL: baseUrl,
});

export const publicFastApiAxiosInstance = axios.create({
  baseURL: fastApiBaseUrl,
});

let refreshPromise: Promise<void> | null = null;
let authFailed = false;

async function resolveAuthToken(): Promise<string> {
  let session = await fetchAuthSession();
  let token = session.tokens?.accessToken.toString();

  if (token) {
    return token;
  }

  if (!refreshPromise) {
    refreshPromise = fetchAuthSession({ forceRefresh: true }).then(async () => {
      await fetchAuthSession();
    });
  }

  await refreshPromise;
  refreshPromise = null;

  session = await fetchAuthSession();
  token = session.tokens?.accessToken.toString();

  if (token) {
    return token;
  }

  throw new Error("Missing auth token");
}

const initializeAxiosInstance = (
  axiosInstance: AxiosInstance,
  allowEmptyString?: boolean,
) => {
  const handleAuthFailure = async () => {
    if (authFailed) return;
    authFailed = true;
    try {
      await Promise.race([
        signOut(),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    } catch {
      // Ignore sign-out errors and continue to redirect.
    }
    window.location.href = "/login";
  };

  axiosInstance.interceptors.request.use(
    async function (config) {
      try {
        const token = await resolveAuthToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch {
        await handleAuthFailure();
        throw new Error("Failed to fetch auth session");
      }
      // Replace all empty string values with null - this makes it so that
      // the backend doesn't store empty strings in the database
      if (!allowEmptyString) {
        assignWith(config.data, config.data, (_, value: unknown) =>
          value !== "" ? value : null,
        );
      }

      return config;
    },
    function (error) {
      const err = error instanceof Error ? error : new Error("Request failed");
      return Promise.reject(err);
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const originalRequest = error?.config as
        | (AxiosRequestConfig & { _retry?: boolean })
        | undefined;
      const err = error instanceof Error ? error : new Error("Request failed");

      if (status === 401 && authFailed) {
        throw err;
      }

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = fetchAuthSession({ forceRefresh: true }).then(
              async () => {
                await fetchAuthSession();
              },
            );
          }
          await refreshPromise;
          refreshPromise = null;

          const { tokens } = await fetchAuthSession();
          if (tokens?.accessToken.toString()) {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${tokens.accessToken.toString()}`;
          }
          return await axiosInstance(originalRequest);
        } catch {
          refreshPromise = null;
          await handleAuthFailure();
        }
      }

      if (status === 401) {
        await handleAuthFailure();
      }

      throw err;
    },
  );
};

export const authenticatedAxiosInstance = axios.create({
  baseURL: baseUrl,
});
initializeAxiosInstance(authenticatedAxiosInstance);

export const authenticatedAxiosInstanceAllowEmptyString = axios.create({
  baseURL: baseUrl,
});
initializeAxiosInstance(authenticatedAxiosInstanceAllowEmptyString, true);

export const authenticatedFastApiAxiosInstance = axios.create({
  baseURL: fastApiBaseUrl,
});
initializeAxiosInstance(authenticatedFastApiAxiosInstance);

export const authenticatedFastApiAxiosInstanceAllowEmptyString = axios.create({
  baseURL: fastApiBaseUrl,
});
initializeAxiosInstance(
  authenticatedFastApiAxiosInstanceAllowEmptyString,
  true,
);
